import { useState, useEffect } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { SidebarNav } from './components/SidebarNav'
import { TopBar } from './components/TopBar'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { ResearchersPage } from './pages/ResearchersPage'
import { PipelinePage } from './pages/PipelinePage'
import { SettingsPage } from './pages/SettingsPage'
import { AddProjectPage } from './pages/AddProjectPage'
import { PeoplePage } from './pages/PeoplePage'
import { PersonDetailPage } from './pages/PersonDetailPage'
import { OrganizationsPage } from './pages/OrganizationsPage'
import { OrganizationDetailPage } from './pages/OrganizationDetailPage'
import { AdminUsersPage } from './pages/AdminUsersPage'
import { useAuth } from './contexts/AuthContext'

// Nav items use static labels; they will be translated in SidebarNav
const getNavItems = (userRole?: string) => {
  const baseItems = [
    { id: 'dashboard', labelKey: 'nav.dashboard', icon: '📊', href: '/' },
    { id: 'projects', labelKey: 'nav.projects', icon: '🔬', href: '/projects' },
    { id: 'people', labelKey: 'nav.people', icon: '👥', href: '/people' },
    { id: 'organizations', labelKey: 'nav.organizations', icon: '🏢', href: '/organizations' },
    { id: 'pipeline', labelKey: 'nav.pipeline', icon: '🔄', href: '/pipeline' },
    { id: 'settings', labelKey: 'nav.settings', icon: '⚙️', href: '/settings' },
  ]

  // Add admin users link only for SUPER_ADMIN
  if (userRole === 'SUPER_ADMIN') {
    baseItems.push({ id: 'admin-users', labelKey: 'nav.admin_users', icon: '🔐', href: '/admin/users' })
  }

  return baseItems
}

// Layout component that includes SidebarNav and auth check
function AppLayout() {
  const { user, isLoading } = useAuth()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('hos-sidebar-collapsed') === 'true'
    } catch {
      return false
    }
  })
  const navItems = getNavItems(user?.role)

  useEffect(() => {
    try {
      localStorage.setItem('hos-sidebar-collapsed', String(sidebarCollapsed))
    } catch { /* noop */ }
  }, [sidebarCollapsed])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-body flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
          <span className="text-body text-text-secondary">Loading...</span>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="flex h-screen bg-bg-body overflow-hidden font-sans">
      {/* Sidebar */}
      <aside className={`flex flex-col bg-[#12121e] border-r border-border-subtle flex-shrink-0 transition-all duration-200 ${sidebarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar'}`}>
        {/* Logo lockup */}
        {sidebarCollapsed ? (
          <div className="flex flex-col items-center py-4 border-b border-border-subtle">
            <img src="/heart-icon.png" alt="The Heart" className="h-7 w-auto" />
          </div>
        ) : (
          <div className="px-3 pt-4 pb-3 border-b border-border-subtle">
            <img src="/heart-of-science-logo.png" alt="The Heart of Science" className="h-12 w-auto" />
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto">
          <SidebarNav items={navItems} collapsed={sidebarCollapsed} />
        </div>

        {/* Collapse toggle + version */}
        <div className="border-t border-border-subtle">
          {!sidebarCollapsed && (
            <div className="px-4 py-1.5 space-y-0.5">
              <div className="text-[10px] text-text-tertiary">v2.0</div>
              {import.meta.env.VITE_DB_LABEL && (
                <div className="text-[9px] text-text-tertiary/60 truncate">
                  DB: {import.meta.env.VITE_DB_LABEL}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            className="flex items-center justify-center w-full h-8 text-text-tertiary hover:text-text-primary hover:bg-bg-surface transition-colors"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="text-[14px]">
              {sidebarCollapsed ? '\u00BB' : '\u00AB'}
            </span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-y-auto bg-bg-body">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

// Create router with all routes
const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'projects/new', element: <AddProjectPage /> },
      { path: 'projects/:id', element: <ProjectsPage /> },
      { path: 'researchers', element: <ResearchersPage /> },
      { path: 'people', element: <PeoplePage /> },
      { path: 'people/:id', element: <PersonDetailPage /> },
      { path: 'organizations', element: <OrganizationsPage /> },
      { path: 'organizations/:id', element: <OrganizationDetailPage /> },
      { path: 'pipeline', element: <PipelinePage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'admin/users', element: <AdminUsersPage /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </LanguageProvider>
  )
}
